import React from 'react'
import type { Owner } from '../utils/checkinFeatures'

interface FeatureLockBadgeProps {
    /** Hover text for a value the user set. Defaults to the wording the
     *  project and feature chips in `CheckinItem` have always carried. */
    title?: string
    /** Who set the value. A Genie value reads "Set by Genie" whatever `title`
     *  says, since each site's own wording describes the user's act. */
    owner?: Owner
    /** Extra classes on the wrapping span — spacing differs between an
     *  inline badge (inside a chip's label) and a standalone one (beside a
     *  list of chips). */
    className?: string
}

/**
 * The padlock that says "someone set this value; a re-parse leaves it alone".
 *
 * One component rather than three copies of the same `<svg>`, because the
 * badge means one thing wherever it appears: the value is owned, by the user
 * or by Genie (`ownerOf`). The two share the glyph and differ only in hover
 * text. It is drawn beside a locked project, inside a locked feature-value
 * chip, and — at list level — beside a locked People/Locations chip strip. The
 * list case is deliberately once per feature, not once per chip: the lock is
 * a property of the feature on that check-in (see `docs/list_tagging.md`),
 * so a padlock on every chip would claim a per-value granularity that does
 * not exist.
 */
const FeatureLockBadge: React.FC<FeatureLockBadgeProps> = ({
    title = 'Manually set',
    owner = 'user',
    className = 'ml-1',
}) => (
    <span
        data-testid="feature-lock-badge"
        className={`text-gray-500 ${className}`}
        title={owner === 'assistant' ? 'Set by Genie' : title}
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5 inline"
            viewBox="0 0 20 20"
            fill="currentColor"
        >
            <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
            />
        </svg>
    </span>
)

export default FeatureLockBadge
